import React from 'react';
import { Badge } from 'features/catalog/useBadges';
import { Profile } from 'features/profiles/useProfiles';
import { compareBadgesByScoreAsc } from 'features/catalog/rarity';
import { BadgeThumb } from 'shared/components/BadgeThumb';
import './ParticipantCard.css';
import { RankPrefix } from 'shared/components/RankPrefix';

interface ParticipantCardProps {
  profile: Profile;
  badgesMap: Map<string, Badge>;
  onClick: () => void;
  subtext?: string;
  variant?: 'default' | 'stats';
  /** 1-based global leaderboard rank (medal / #N before the name). */
  rank?: number;
  /** Signed-in viewer owns this badge — Claimed-by row with hash wash (claim-order position). */
  isYou?: boolean;
  /** Badge Hash wash — always on for `isYou`, hover-reveal otherwise. */
  highlightGradient?: string | null;
  // New props for badge tooltip
  showBadgeTooltip?: boolean;
  badgeTooltipIDs?: string[];
  onBadgeClick?: (badge: Badge) => void;
  showUnclaimedBadges?: boolean;
}

const NUM_ROWS_IN_TOOLTIP = 3;
const NUM_COLS_IN_TOOLTIP = 4;
const NUM_BADGES_IN_TOOLTIP = NUM_ROWS_IN_TOOLTIP * NUM_COLS_IN_TOOLTIP;
const EMPTY_BADGE_IDS: string[] = [];

export const ParticipantCard: React.FC<ParticipantCardProps> = ({
  profile,
  badgesMap,
  onClick,
  subtext,
  variant = 'default',
  rank,
  isYou = false,
  highlightGradient = null,
  showBadgeTooltip = false,
  badgeTooltipIDs = EMPTY_BADGE_IDS,
  onBadgeClick,
  showUnclaimedBadges = false,
}) => {
  const topBadge = React.useMemo(() => {
    let coolest: Badge | undefined;
    for (const badgeID of profile.badgesIDs) {
      const badge = badgesMap.get(badgeID);
      if (!badge) continue;
      if (!coolest || (badge.badgeScore ?? 0) < (coolest.badgeScore ?? 0)) {
        coolest = badge;
      }
    }
    return coolest;
  }, [profile.badgesIDs, badgesMap]);

  // Get badges owned by this profile in the current group (rarest / lowest badgeScore first).
  const groupBadgesOwned = React.useMemo((): Badge[] => {
    if (!showBadgeTooltip) return [];
    if (showUnclaimedBadges) {
      return badgeTooltipIDs
        .map((badgeID) => badgesMap.get(badgeID))
        .filter((badge): badge is Badge => !!badge)
        .sort(compareBadgesByScoreAsc);
    }

    const tooltipIDs = new Set(badgeTooltipIDs);
    return profile.badgesIDs
      .filter((badgeID) => tooltipIDs.has(badgeID))
      .map((badgeID) => badgesMap.get(badgeID))
      .filter((badge): badge is Badge => !!badge)
      .sort(compareBadgesByScoreAsc);
  }, [showBadgeTooltip, showUnclaimedBadges, badgeTooltipIDs, profile.badgesIDs, badgesMap]);

  const collectedLine = `${profile.badgesIDs.length}/${badgesMap.size} badges`;

  return (
    <div
      className={`participant-card variant-${variant}${showBadgeTooltip ? ' with-tooltip' : ''}${
        isYou ? ' participant-card--you' : ''
      }${highlightGradient ? ' participant-card--hash' : ''}`}
      style={
        highlightGradient
          ? ({
              '--participant-hash-gradient': highlightGradient,
            } as React.CSSProperties)
          : undefined
      }
      onClick={onClick}
    >
      {topBadge ? (
        <BadgeThumb
          badge={topBadge}
          size="sm"
          alt={topBadge.name}
          imageClassName="participant-image"
          showRarity={false}
        />
      ) : (
        <div className="participant-image" aria-hidden />
      )}
      <div className="participant-card-text">
        <h3 className="profile-header">
          <span className="profile-name">
            {rank != null && rank > 0 ? (
              <>
                <RankPrefix rank={rank} />{' '}
              </>
            ) : null}
            {profile.name}
          </span>
          {isYou ? <span className="participant-you-tag">You</span> : null}
        </h3>
        <p className="participant-badges">{subtext ?? collectedLine}</p>
      </div>

      {/* Show tooltip always when showBadgeTooltip is true for debugging */}
      {showBadgeTooltip && (
        <div className="profile-badge-tooltip">
          <div className="journey-badges-grid">
            {groupBadgesOwned
              .slice(
                0,
                groupBadgesOwned.length === NUM_BADGES_IN_TOOLTIP ? NUM_BADGES_IN_TOOLTIP : NUM_BADGES_IN_TOOLTIP - 1
              )
              .map((badge) => (
                <button
                  key={badge.id}
                  type="button"
                  className="journey-badge-tooltip-btn"
                  aria-label={badge.name}
                  onClick={(e) => {
                    e.stopPropagation();
                    onBadgeClick?.(badge);
                  }}
                >
                  <BadgeThumb badge={badge} size="sm" alt={badge.name} imageClassName="journey-badge-tooltip-image" />
                </button>
              ))}
            {groupBadgesOwned.length > NUM_BADGES_IN_TOOLTIP && (
              <div
                className="expand-on-hover"
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                }}
              >
                <h1>+</h1>
                <h1 style={{ letterSpacing: '0.1rem' }}>{groupBadgesOwned.length - NUM_BADGES_IN_TOOLTIP - 1}</h1>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
